import React, { useEffect, useRef, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { Chips } from 'primereact/chips';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  icon?: React.ReactElement;
  readOnly?: boolean;
  required?: boolean;
  errorMessage?: string;
  dataTestId?: string;
  dataTestIdInput?: string;
  dataTestIdSave?: string;
  dataTestIdClose?: string;
}

const InputChipsTag: React.FC<ComponentProps> = ({
  secured,
  label,
  value,
  onChange,
  icon,
  readOnly,
  required = false,
  errorMessage,
  dataTestId,
  dataTestIdInput,
  dataTestIdSave,
  dataTestIdClose,
}) => {
  const [internalValue, setInternalValue] = useState<string[]>(value ?? []);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setInternalValue(value ?? []), [value]);

  const pane = () => {
    return (
      <div className="flex flex-col gap-1">
        {label && <FieldLabel label={label} />}
        <Chips inputRef={inputRef} separator="," value={internalValue} onChange={e => setInternalValue(e.value ?? [])} data-testid={dataTestIdInput} />
      </div>
    );
  };

  return (
    <>
      <ButtonTag
        secured={secured}
        label={label}
        icon={icon}
        value={internalValue?.join(', ')}
        actionIcon={<SvgTagEdit />}
        actionPane={pane()}
        onSave={onSuccess => {
          // A value typed into the chips input but not yet confirmed with Enter must not be lost on save.
          const pending = inputRef.current?.value?.trim();
          const merged = [...internalValue, ...(pending ? [pending] : [])]
            .map(v => v.trim())
            .filter(v => v.length > 0)
            .filter((v, index, arr) => arr.indexOf(v) === index);
          if (inputRef.current) {
            inputRef.current.value = '';
          }
          setInternalValue(merged);
          onChange(merged);
          onSuccess();
        }}
        readonly={readOnly}
        required={required}
        errorMessage={errorMessage}
        autoClose
        dataTestId={dataTestId}
        dataTestIdSave={dataTestIdSave}
        dataTestIdClose={dataTestIdClose}
      />
    </>
  );
};

export default InputChipsTag;
